import { useState, useRef, useCallback, useMemo } from 'react';
import { Upload, AlertCircle, CheckCircle, XCircle, Loader2, FileText, ArrowRight } from 'lucide-react';
import { importAssets } from '../api/import';

const KNOWN_FIELDS = [
  { key: 'hostname', label: 'Hostname', required: true },
  { key: 'ip_address', label: 'IP Address', required: false },
  { key: 'mac_address', label: 'MAC Address', required: false },
  { key: 'device_type', label: 'Device Type', required: false },
  { key: 'hardware_vendor', label: 'Hardware Vendor', required: false },
  { key: 'location', label: 'Location', required: false },
];

const REQUIRED_FIELD_KEYS = KNOWN_FIELDS.filter((f) => f.required).map((f) => f.key);

const CSV_FORMAT_EXAMPLE = {
  headers: ['hostname', 'ip_address', 'mac_address', 'device_type', 'hardware_vendor', 'location'],
  rows: [
    ['AP-Floor1-01', '10.1.1.5', '00:1a:2b:3c:4d:5e', 'network_device', 'Ruijie', 'Building-A-Floor1'],
    ['core-sw-01', '10.0.0.1', '', 'switch', 'Cisco', 'DataCenter-01'],
  ],
};

const FIELD_PATTERNS = {
  hostname: ['hostname', 'host', 'name', 'server_name', 'computer_name', 'machine'],
  ip_address: ['ip_address', 'ip', 'ipaddress', 'address', 'ip_addr'],
  mac_address: ['mac_address', 'mac', 'macaddress', 'mac_addr', 'physical_address'],
  device_type: ['device_type', 'type', 'device', 'asset_type', 'category'],
  hardware_vendor: ['hardware_vendor', 'vendor', 'manufacturer', 'make', 'hardware'],
  location: ['location', 'site', 'room', 'rack', 'position', 'datacenter'],
};

function splitCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else if (ch === '\n' || ch === '\r') {
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return { headers: [], rows: [] };

  const headers = splitCSVLine(lines[0]);
  const rows = lines.slice(1).map((line) => splitCSVLine(line));

  return { headers, rows };
}

function autoDetectMapping(headers) {
  const mapping = {};
  const used = new Set();

  headers.forEach((header) => {
    const lower = header.toLowerCase().replace(/[^a-z0-9_]/g, '');
    for (const [field, patterns] of Object.entries(FIELD_PATTERNS)) {
      if (used.has(field)) continue;
      const match = patterns.some(
        (p) => lower === p || lower.includes(p) || p.includes(lower)
      );
      if (match) {
        mapping[header] = field;
        used.add(field);
        break;
      }
    }
  });

  return mapping;
}

export default function ImportAssets() {
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  const previewRows = useMemo(() => csvRows.slice(0, 5), [csvRows]);

  const fieldToHeader = useMemo(() => {
    const rev = {};
    Object.entries(mapping).forEach(([header, field]) => {
      if (field) rev[field] = header;
    });
    return rev;
  }, [mapping]);

  const isValid = useMemo(() => {
    if (csvHeaders.length === 0) return false;
    const mappedFields = Object.values(mapping).filter(Boolean);
    return REQUIRED_FIELD_KEYS.every((req) => mappedFields.includes(req));
  }, [csvHeaders, mapping]);

  const previewMappedColumns = useMemo(() => {
    return KNOWN_FIELDS.filter((f) => fieldToHeader[f.key]).map((f) => ({
      field: f,
      header: fieldToHeader[f.key],
    }));
  }, [fieldToHeader]);

  // ── File reading ──────────────────────────────────────────────────

  const processFile = useCallback((selectedFile) => {
    if (!selectedFile) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const parsed = parseCSV(text);

        if (parsed.headers.length === 0 || parsed.rows.length === 0) {
          setError('CSV file appears empty or has no data rows.');
          return;
        }

        setFile(selectedFile);
        setCsvHeaders(parsed.headers);
        setCsvRows(parsed.rows);
        setResults(null);
        setError(null);

        const detected = autoDetectMapping(parsed.headers);
        setMapping(detected);
      } catch (err) {
        setError('Failed to parse CSV file. Please check the file format.');
      }
    };
    reader.onerror = () => {
      setError('Failed to read file.');
    };
    reader.readAsText(selectedFile);
  }, []);

  const handleFileSelect = useCallback(
    (e) => {
      const selected = e.target.files?.[0];
      processFile(selected);
      e.target.value = '';
    },
    [processFile]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      const dropped = e.dataTransfer?.files?.[0];
      if (dropped?.type === 'text/csv' || dropped?.name?.endsWith('.csv')) {
        processFile(dropped);
      } else {
        setError('Please drop a valid .csv file.');
      }
    },
    [processFile]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleMappingChange = useCallback((header, field) => {
    setMapping((prev) => ({ ...prev, [header]: field }));
  }, []);

  const handleClear = useCallback(() => {
    setFile(null);
    setCsvHeaders([]);
    setCsvRows([]);
    setMapping({});
    setResults(null);
    setError(null);
    setImporting(false);
  }, []);

  const handleImport = useCallback(async () => {
    if (!isValid || importing) return;

    setImporting(true);
    setError(null);
    setResults(null);

    try {
      const rows = csvRows.map((row) => {
        const entry = {};
        csvHeaders.forEach((header, idx) => {
          const field = mapping[header];
          if (field && idx < row.length) {
            entry[field] = row[idx];
          }
        });
        return entry;
      });

      const res = await importAssets(rows);
      setResults(res);
    } catch (err) {
      setError(err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  }, [csvHeaders, csvRows, mapping, isValid, importing]);

  const hasFile = csvHeaders.length > 0;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Import Assets</h1>
        <p className="text-sm text-gray-500 mt-1">
          Bulk-import assets by uploading a CSV file with asset information
        </p>
      </div>

      {/* ── CSV Format Example ─────────────────────────────────── */}
      <div className="bg-dark-800 border border-dark-700/50 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <FileText className="w-5 h-5 text-orange-400" />
          <h2 className="text-lg font-semibold text-white">CSV Format</h2>
        </div>

        <p className="text-sm text-gray-400 mb-3">
          Your CSV file must use the first row as column headers.
          The only required column is <span className="text-orange-400 font-mono text-xs bg-dark-900 px-1.5 py-0.5 rounded">hostname</span>
          — all other columns are optional. Columns can be in any order; the system will auto-detect them.
        </p>

        <div className="overflow-x-auto rounded-lg border border-dark-700/50">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-dark-900/80 border-b border-dark-700">
                {CSV_FORMAT_EXAMPLE.headers.map((h) => {
                  const isRequired = KNOWN_FIELDS.find((f) => f.key === h)?.required;
                  return (
                    <th
                      key={h}
                      className="text-left py-2.5 px-3 text-gray-400 font-mono text-xs font-medium whitespace-nowrap"
                    >
                      {h}
                      {isRequired && <span className="text-red-400 ml-1">*</span>}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {CSV_FORMAT_EXAMPLE.rows.map((row, rowIdx) => (
                <tr
                  key={rowIdx}
                  className={`border-b border-dark-800/50 ${
                    rowIdx % 2 === 0 ? 'bg-dark-900/30' : ''
                  }`}
                >
                  {row.map((cell, cellIdx) => {
                    const isRequired = KNOWN_FIELDS.find((f) => f.key === CSV_FORMAT_EXAMPLE.headers[cellIdx])?.required;
                    return (
                      <td
                        key={cellIdx}
                        className={`py-2 px-3 font-mono text-xs whitespace-nowrap ${
                          cell
                            ? 'text-gray-300'
                            : 'text-gray-700'
                        } ${isRequired && !cell ? 'bg-red-500/5' : ''}`}
                      >
                        {cell || (
                          <span className="italic">empty</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-3 flex flex-wrap gap-4 text-xs text-gray-500">
          <span>
            <span className="text-red-400 mr-1">*</span>Required column
          </span>
          {KNOWN_FIELDS.filter((f) => !f.required).map((f) => (
            <span key={f.key}>
              <span className="text-gray-600">{f.label}</span>{' '}
              <span className="text-gray-700">(optional)</span>
            </span>
          ))}
        </div>
      </div>

      <div className="bg-dark-800 border border-dark-700/50 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Upload className="w-5 h-5 text-orange-400" />
          <h2 className="text-lg font-semibold text-white">Upload CSV</h2>
        </div>

        {!hasFile ? (
          <>
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              className={`relative flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-12 cursor-pointer transition-colors ${
                dragOver
                  ? 'border-orange-500 bg-orange-500/10'
                  : 'border-dark-600 hover:border-dark-500 bg-dark-900/50'
              }`}
            >
              <Upload
                className={`w-10 h-10 mb-3 transition-colors ${
                  dragOver ? 'text-orange-400' : 'text-gray-600'
                }`}
              />
              <p className="text-sm text-gray-400 font-medium">
                {dragOver ? 'Drop your file here' : 'Drag & drop your CSV file here'}
              </p>
              <p className="text-xs text-gray-600 mt-1">or click to browse</p>
              <p className="text-xs text-gray-700 mt-3">Accepts .csv files only</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          </>
        ) : (
          <div className="flex items-center justify-between bg-dark-900/60 border border-dark-700 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-orange-400" />
              <div>
                <p className="text-sm font-medium text-white">{file?.name}</p>
                <p className="text-xs text-gray-500">
                  {csvHeaders.length} columns &middot; {csvRows.length} rows
                </p>
              </div>
            </div>
            <button
              onClick={handleClear}
              className="text-xs text-gray-500 hover:text-red-400 transition-colors"
            >
              Remove
            </button>
          </div>
        )}
      </div>

      {/* ── Column Mapping ─────────────────────────────────────── */}
      {hasFile && (
        <div className="bg-dark-800 border border-dark-700/50 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <ArrowRight className="w-5 h-5 text-orange-400" />
            <h2 className="text-lg font-semibold text-white">Column Mapping</h2>
          </div>

          <div className="space-y-3">
            {csvHeaders.map((header, idx) => {
              const currentField = mapping[header] || '';
              const isAutoDetected = mapping[header] && autoDetectMapping(csvHeaders)[header] === mapping[header];

              return (
                <div
                  key={idx}
                  className="flex items-center gap-3 bg-dark-900/40 border border-dark-700/50 rounded-lg px-4 py-3"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{header}</p>
                    <p className="text-xs text-gray-600">Column {idx + 1}</p>
                  </div>

                  <ArrowRight className="w-4 h-4 text-gray-600 shrink-0" />

                  <div className="w-48 shrink-0">
                    <select
                      value={currentField}
                      onChange={(e) => handleMappingChange(header, e.target.value)}
                      className="w-full bg-dark-900 border border-dark-700 text-sm text-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500/50 transition-colors appearance-none cursor-pointer"
                    >
                      <option value="">— Skip column —</option>
                      {KNOWN_FIELDS.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label}{f.required ? ' *' : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {currentField && isAutoDetected && (
                    <span className="text-xs text-emerald-500 shrink-0">Auto</span>
                  )}
                  {currentField && !isAutoDetected && (
                    <span className="text-xs text-orange-500 shrink-0">Manual</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {hasFile && previewMappedColumns.length > 0 && (
        <div className="bg-dark-800 border border-dark-700/50 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-5 h-5 text-orange-400" />
            <h2 className="text-lg font-semibold text-white">
              Preview{' '}
              <span className="text-sm font-normal text-gray-500">
                (first {Math.min(5, csvRows.length)} of {csvRows.length} rows)
              </span>
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-700">
                  <th className="text-left py-2 px-3 text-gray-500 font-medium text-xs uppercase tracking-wider">
                    #
                  </th>
                  {previewMappedColumns.map(({ field, header }) => (
                    <th
                      key={field.key}
                      className="text-left py-2 px-3 text-gray-500 font-medium text-xs uppercase tracking-wider"
                    >
                      {field.label}
                      {field.required && (
                        <span className="text-red-400 ml-1">*</span>
                      )}
                      <span className="block text-gray-700 font-normal normal-case">
                        ← {header}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, rowIdx) => (
                  <tr
                    key={rowIdx}
                    className={`border-b border-dark-800/50 ${
                      rowIdx % 2 === 0 ? 'bg-dark-900/30' : ''
                    }`}
                  >
                    <td className="py-2 px-3 text-gray-600 text-xs">{rowIdx + 1}</td>
                    {previewMappedColumns.map(({ field }) => {
                      const header = fieldToHeader[field.key];
                      const colIdx = header ? csvHeaders.indexOf(header) : -1;
                      const value = colIdx >= 0 && colIdx < row.length ? row[colIdx] : '';
                      return (
                        <td key={field.key} className="py-2 px-3 text-gray-300">
                          {value || <span className="text-gray-700 italic">empty</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {hasFile && (
        <div className="flex items-center gap-4">
          <button
            onClick={handleImport}
            disabled={!isValid || importing}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium text-sm transition-all ${
              isValid && !importing
                ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-500/20'
                : 'bg-dark-700 text-gray-500 cursor-not-allowed'
            }`}
          >
            {importing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Import {csvRows.length} Assets
              </>
            )}
          </button>

          {!isValid && (
            <p className="text-xs text-red-400 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" />
              Map at least one column to Hostname (required)
            </p>
          )}
        </div>
      )}

      {results && (
        <div className="bg-dark-800 border border-dark-700/50 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
            <h2 className="text-lg font-semibold text-white">Import Results</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="bg-dark-900/60 border border-dark-700/50 rounded-lg p-4 text-center">
              <CheckCircle className="w-6 h-6 text-emerald-400 mx-auto mb-1" />
              <p className="text-2xl font-bold text-white">{results.created ?? 0}</p>
              <p className="text-xs text-gray-500">Created</p>
            </div>
            <div className="bg-dark-900/60 border border-dark-700/50 rounded-lg p-4 text-center">
              <XCircle className="w-6 h-6 text-gray-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-white">{results.skipped ?? 0}</p>
              <p className="text-xs text-gray-500">Skipped</p>
            </div>
            <div className="bg-dark-900/60 border border-dark-700/50 rounded-lg p-4 text-center">
              <AlertCircle className="w-6 h-6 text-red-400 mx-auto mb-1" />
              <p className="text-2xl font-bold text-white">{results.errors?.length ?? 0}</p>
              <p className="text-xs text-gray-500">Errors</p>
            </div>
          </div>

          {results.errors?.length > 0 && (
            <div className="bg-dark-900/40 border border-dark-700/50 rounded-lg p-4 max-h-48 overflow-y-auto">
              <p className="text-xs font-medium text-red-400 mb-2">Row Errors</p>
              <div className="space-y-1">
                {results.errors.map((err, i) => (
                  <p key={i} className="text-xs text-gray-400 font-mono">
                    Row {err.row ?? i + 1}: {err.error || err.message || 'Unknown error'}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {error && !results && (
        <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-300">{error}</p>
        </div>
      )}
    </div>
  );
}
